import React from 'react';
import { Flex, Select, Stack, Text } from '@mantine/core';
import { CoverPageBeforeToggle } from 'src/company/templates/components/CoverPageBeforeToggle';
import { DEFAULT_TABLE_OF_CONTENTS_DEPTH, TableOfContentsDepth } from 'src/company/templates/types';

const DEPTH_OPTIONS = [
  { value: '1', label: 'Heading 1 only' },
  { value: '2', label: 'Headings 1–2' },
  { value: '3', label: 'Headings 1–3' },
  { value: '4', label: 'Headings 1–4' },
];

type TableOfContentsSection = {
  id: string;
  tableOfContentsDepth?: number;
  addCoverPageBeforeSection?: boolean;
};

type Props<T extends TableOfContentsSection> = {
  section: T;
  onUpdateSection: (sectionId: string, updates: Partial<T>) => void;
};

export function TableOfContentsSettings<T extends TableOfContentsSection>({
  section,
  onUpdateSection,
}: Props<T>) {
  const depth = section.tableOfContentsDepth ?? DEFAULT_TABLE_OF_CONTENTS_DEPTH;

  const handleDepthChange = (value: string | null) => {
    if (!value) {
      return;
    }
    onUpdateSection(section.id, {
      tableOfContentsDepth: Number(value) as TableOfContentsDepth,
    } as Partial<T>);
  };

  const handleToggleCoverPage = (addCoverPageBeforeSection: boolean) => {
    onUpdateSection(section.id, { addCoverPageBeforeSection } as Partial<T>);
  };

  return (
    <Stack gap="md" style={{ minWidth: 0 }}>
      <Text size="sm" c="gray.7">
        This section produces a table of contents in Word at this point in the report. Adding it
        means the report no longer gets a cover page and contents on the first page automatically.
      </Text>

      <Flex gap="md" align="flex-end" wrap="wrap">
        <CoverPageBeforeToggle
          checked={section.addCoverPageBeforeSection ?? false}
          onChange={handleToggleCoverPage}
        />
        <Select
          label="Contents depth"
          data={DEPTH_OPTIONS}
          value={String(depth)}
          onChange={handleDepthChange}
          size="xs"
          w={200}
          allowDeselect={false}
        />
      </Flex>
    </Stack>
  );
}
